import React, { useState, useRef, useEffect } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowUpRight, Zap, Heart } from 'lucide-react';
import './Services.css';

const Services = () => {
  const [, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [activeService, setActiveService] = useState(0);
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);

  const services = [
    {
      id: 'asma',
      title: 'ASMA',
      subtitle: 'Enterprise Automation',
      tagline: 'The Future of Business Intelligence',
      description: 'Revolutionary AI-driven automation solutions that transform how enterprises operate in the digital age.',
      longDescription: 'Asma represents the pinnacle of enterprise automation technology. Our advanced AI systems learn, adapt, and optimize your business processes in real-time, delivering unprecedented efficiency and intelligence to your operations.',
      features: [
        'Intelligent Process Automation',
        'Custom AI Model Development',
        'Real-time Analytics Dashboard',
        'Predictive Business Intelligence',
        'Seamless System Integration',
        'Advanced Workflow Orchestration'
      ],
      stats: [
        { label: 'Efficiency Increase', value: '300%' },
        { label: 'Processing Speed', value: '10x' },
        { label: 'Cost Reduction', value: '60%' }
      ],
      link: 'https://asma.astral-ai.io',
      icon: Zap,
      color: '#d3c5aa',
      bgColor: 'rgba(211, 197, 170, 0.05)',
      image: '/api/placeholder/600/400'
    },
    {
      id: 'gaia',
      title: 'GAIA',
      subtitle: 'Mystical Guidance',
      tagline: 'Where Ancient Wisdom Meets Modern Technology',
      description: 'Ancient tarot wisdom reimagined through cutting-edge technology for modern spiritual guidance.',
      longDescription: 'Gaia bridges the mystical and digital realms, offering profound spiritual insights through the timeless art of tarot. Our platform combines centuries-old wisdom with contemporary convenience.',
      features: [
        'Personal Tarot Consultations',
        'WhatsApp Integration',
        'Daily Spiritual Guidance',
        'Relationship & Career Insights',
        'Meditation & Mindfulness',
        'Spiritual Journey Tracking'
      ],
      stats: [
        { label: 'Accuracy Rate', value: '94%' },
        { label: 'Monthly Users', value: '10K+' },
        { label: 'Satisfaction', value: '98%' }
      ],
      link: 'https://gaia.astral-ai.io',
      icon: Heart,
      color: '#c4b593',
      bgColor: 'rgba(196, 181, 147, 0.05)',
      image: '/api/placeholder/600/400'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveService((prev) => (prev + 1) % services.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [services.length]);

  return (
    <section className="services-magazine" ref={containerRef} id="services">
      <motion.div 
        className="services-container"
        style={{ y, opacity }}
      >


        {/* Service Showcase */}
        <div className="service-showcase">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeService}
              className="showcase-content"
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
            >
              {/* Left Column - Visual */}
              <div className="showcase-visual">
                <motion.div 
                  className="service-image"
                  initial={{ scale: 1.1 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 1.2 }}
                  style={{ backgroundColor: services[activeService].bgColor }}
                >
                  <div className="image-overlay">
                    {React.createElement(services[activeService].icon, { 
                      size: 80, 
                      color: services[activeService].color 
                    })}
                  </div>
                  <div className="image-stats">
                    {services[activeService].stats.map((stat, index) => (
                      <motion.div
                        key={index}
                        className="stat-item"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 + index * 0.1, duration: 0.6 }}
                      >
                        <span className="stat-value" style={{ color: services[activeService].color }}>
                          {stat.value}
                        </span>
                        <span className="stat-label">{stat.label}</span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </div>

              {/* Right Column - Content */}
              <div className="showcase-content-area">
                <motion.div
                  className="service-badge-magazine"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                  style={{ borderColor: services[activeService].color }}
                >
                  {services[activeService].title}
                </motion.div>

                <motion.h3
                  className="service-title-magazine"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  {services[activeService].tagline}
                </motion.h3>

                <motion.p
                  className="service-description-magazine"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  {services[activeService].longDescription}
                </motion.p>

                <motion.div
                  className="features-magazine"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  <h4>Key Features</h4>
                  <div className="features-grid-magazine">
                    {services[activeService].features.map((feature, index) => (
                      <motion.div
                        key={index}
                        className="feature-magazine"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.6 + index * 0.05 }}
                      >
                        <div 
                          className="feature-bullet"
                          style={{ backgroundColor: services[activeService].color }}
                        />
                        <span>{feature}</span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>

                <motion.div
                  className="cta-magazine"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                >
                  <a 
                    href={services[activeService].link}
                    className="cta-button-magazine"
                    style={{ borderColor: services[activeService].color }}
                  >
                    <span>EXPLORE {services[activeService].title}</span>
                    <ArrowUpRight size={20} />
                  </a>
                </motion.div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Service Navigation */}
        <motion.div
          className="service-navigation"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          {services.map((service, index) => (
            <button
              key={service.id}
              className={`nav-item ${index === activeService ? 'active' : ''}`}
              onClick={() => setActiveService(index)}
            >
              <div className="nav-number">
                {String(index + 1).padStart(2, '0')}
              </div>
              <div className="nav-content">
                <span className="nav-title">{service.title}</span>
                <span className="nav-subtitle">{service.subtitle}</span>
              </div>
              <div 
                className="nav-indicator"
                style={{ backgroundColor: index === activeService ? service.color : 'transparent' }}
              />
            </button>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Services; 